/**
 * FieldSelector
 * Mobile card view field selection component for On-Time Dispatch sections
 * Allows users to select up to 3 fields to display on cards
 * 
 * Features:
 * - Metric pills with max 3 selection limit
 * - Reset and Apply buttons
 * - Expandable interface
 * - Dark mode support via `isDark` prop (phone-only, scoped to caller)
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FieldDefinition {
  id: string;
  label: string;
  shortLabel?: string; // Shorter version for mobile display
}

export interface FieldSelectorProps {
  /** Whether the selector is expanded */
  readonly isExpanded: boolean;
  /** Array of selected field IDs */
  readonly selectedFields: string[];
  /** Available fields to choose from */
  readonly availableFields: FieldDefinition[];
  /** Maximum number of fields allowed */
  readonly maxFields?: number;
  /** Called when the header is tapped to toggle expansion */
  readonly onToggle: () => void;
  /** Called when a field is toggled */
  readonly onFieldToggle: (fieldId: string) => void;
  /** Called when Reset is pressed */
  readonly onReset: () => void;
  /** Called when Apply is pressed */
  readonly onApply: () => void;
  /** Optional title for the selector (e.g., "Location Summary Fields") */
  readonly title?: string;
  /**
   * When true, applies dark-surface styles.
   * Controlled by the parent (phone dark mode only) — does not affect other screens.
   */
  readonly isDark?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const FieldSelector: React.FC<FieldSelectorProps> = ({
  isExpanded,
  selectedFields,
  availableFields,
  maxFields = 3,
  onToggle,
  onFieldToggle,
  onReset,
  onApply,
  title = 'Field Selector',
  isDark = false,
}) => {
  const selectedCount = selectedFields.length;

  // Dark-mode colour tokens (kept local — does not touch shared theme system)
  const DK = {
    surface: '#202020',
    elevated: '#2B2B2B',
    border: '#404040',
    divider: '#363636',
    textPrimary: '#F2F2F2',
    textSecondary: '#B0B0B0',
    textDisabled: '#6E6E6E',
    brand: '#3D8BFF',
    pillUnselected: '#2B2B2B',
    pillUnselectedText: '#B0B0B0',
    pillDisabledText: '#6E6E6E',
  };

  return (
    <View
      style={[
        styles.container,
        isDark && { backgroundColor: DK.surface, borderWidth: 1, borderColor: DK.border },
      ]}
    >
      {/* Header */}
      <TouchableOpacity
        style={[styles.header, isDark && { backgroundColor: DK.surface }]}
        onPress={onToggle}
        activeOpacity={0.7}
      >
        <View style={styles.headerLeft}>
          <View style={[styles.iconContainer, isDark && { backgroundColor: DK.brand }]}>
            <MaterialIcons name="tune" size={24} color="#FFFFFF" />
          </View>
          <View style={styles.headerTextContainer}>
            <Text style={[styles.headerTitle, isDark && { color: DK.brand }]}>{title}</Text>
            <Text style={[styles.headerSubtitle, isDark && { color: DK.textSecondary }]}>
              {selectedCount} {selectedCount === 1 ? 'field' : 'fields'} selected
            </Text>
          </View>
        </View>
        <MaterialIcons
          name={isExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
          size={24}
          color={isDark ? DK.brand : '#005CBC'}
        />
      </TouchableOpacity>

      {/* Expanded Content */}
      {isExpanded && (
        <View
          style={[
            styles.content,
            isDark && { borderTopColor: DK.divider, backgroundColor: DK.surface },
          ]}
        >
          {/* Instruction */}
          <Text style={[styles.instruction, isDark && { color: DK.textDisabled }]}>
            Select up to {maxFields} fields to display on cards.
          </Text>

          {/* Field Pills */}
          <View style={styles.fieldsContainer}>
            {availableFields.map((field) => {
              const isSelected = selectedFields.includes(field.id);
              const isDisabled = !isSelected && selectedCount >= maxFields;
              return (
                <TouchableOpacity
                  key={field.id}
                  style={[
                    styles.fieldPill,
                    isDark && { backgroundColor: DK.pillUnselected, borderColor: DK.border },
                    isSelected && styles.fieldPillSelected,
                    isSelected && isDark && { backgroundColor: DK.brand, borderColor: DK.brand },
                    isDisabled && styles.fieldPillDisabled,
                  ]}
                  onPress={() => onFieldToggle(field.id)}
                  disabled={isDisabled}
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.fieldPillText,
                      isDark && { color: DK.pillUnselectedText },
                      isSelected && styles.fieldPillTextSelected,
                      isDisabled && styles.fieldPillTextDisabled,
                      isDisabled && isDark && { color: DK.pillDisabledText },
                    ]}
                  >
                    {field.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Selection Counter */}
          <Text style={[styles.selectionCounter, isDark && { color: DK.textSecondary }]}>
            <Text style={[styles.selectionCounterHighlight, isDark && { color: DK.brand }]}>
              {selectedCount}
            </Text>
            {' '}of {maxFields} selected
          </Text>

          {/* Action Buttons */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[
                styles.resetButton,
                isDark && {
                  backgroundColor: 'transparent',
                  borderColor: DK.brand,
                },
              ]}
              onPress={onReset}
              activeOpacity={0.7}
            >
              <Text style={[styles.resetButtonText, isDark && { color: DK.brand }]}>
                Reset
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.applyButton,
                isDark && { backgroundColor: DK.brand },
              ]}
              onPress={onApply}
              activeOpacity={0.7}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#005CBC',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextContainer: {
    gap: 2,
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
    color: '#005CBC',
  },
  headerSubtitle: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  // Content
  content: {
    paddingHorizontal: 14,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: '#E9ECEF',
  },
  // Instruction
  instruction: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    fontStyle: 'italic',
    color: '#9E9E9E',
    marginBottom: 12,
    marginTop: 10,
    lineHeight: 20,
  },
  // Fields
  fieldsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 10,
  },
  fieldPill: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    backgroundColor: '#E9ECEF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  fieldPillSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  fieldPillDisabled: {
    opacity: 0.45,
  },
  fieldPillText: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#6C757D',
  },
  fieldPillTextSelected: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
  },
  fieldPillTextDisabled: {
    color: '#9AA1AB',
  },
  // Selection Counter
  selectionCounter: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
    textAlign: 'right',
    marginBottom: 10,
  },
  selectionCounterHighlight: {
    color: '#005CBC',
    fontFamily: FontFamily.bold,
  },
  // Actions
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  resetButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#005CBC',
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#005CBC',
    fontWeight: '700',
  },
  applyButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: '#005CBC',
    borderRadius: 24,
    alignItems: 'center',
  },
  applyButtonText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

FieldSelector.displayName = 'FieldSelector';

export default React.memo(FieldSelector);
